import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch, ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import { Combobox, type ComboboxOption } from './combobox';
import {
  ProductHistoryTab,
  ProductInventoryTab,
  ProductThresholdTab,
} from './product-detail-tabs';
import { UnitEditor } from './unit-editor';
import type { PresetItem, ProductDetail, SupplierItem } from './types';

type TabKey =
  'basic' | 'units' | 'suppliers' | 'expiration' | 'inventory' | 'threshold' | 'history';

async function searchPresets(path: string, query: string): Promise<ComboboxOption[]> {
  const res = await apiFetch<{ items: PresetItem[] }>(`${path}?query=${encodeURIComponent(query)}`);
  return res.items.map((i) => ({ id: i.id, label: i.name }));
}
async function createPreset(path: string, name: string): Promise<ComboboxOption> {
  const res = await apiFetch<{ id: string }>(path, { method: 'POST', body: { name } });
  return { id: res.id, label: name };
}

/**
 * 产品详情（S8）：整页而非抽屉——信息量大。标签页顺序与内容与 02-ux-ia.md
 * S8 表格一致；库存/阈值/历史三个标签在 M5/M8 数据就位后接真实数据
 * （product-detail-tabs.tsx），不再是 M4 时期的里程碑占位空状态。
 */
export function ProductDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const canEdit = session?.permissions.includes(PERMISSIONS.PRODUCT_EDIT) ?? false;
  const [tab, setTab] = useState<TabKey>('basic');

  const { data: product, isPending } = useQuery({
    queryKey: ['products', id],
    queryFn: () => apiFetch<ProductDetail>(`/products/${id}`),
    enabled: !!id,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['products', id] });

  const archive = useMutation({
    mutationFn: () => apiFetch(`/products/${id}/archive`, { method: 'POST' }),
    onSuccess: () => refresh(),
  });

  /** archive() 此前一直缺失的对称端点——单件恢复（0023） */
  const restore = useMutation({
    mutationFn: () => apiFetch(`/products/${id}/restore`, { method: 'POST' }),
    onSuccess: () => refresh(),
  });

  const dismissReview = useMutation({
    mutationFn: () => apiFetch(`/products/${id}/dismiss-review`, { method: 'POST' }),
    onSuccess: () => refresh(),
  });

  if (isPending || !product) return <p>{t('app.loading')}</p>;

  const tabs: Array<{ key: TabKey; labelKey: string }> = [
    { key: 'basic', labelKey: 'products.tabBasic' },
    { key: 'units', labelKey: 'products.tabUnits' },
    { key: 'suppliers', labelKey: 'products.tabSuppliers' },
    { key: 'expiration', labelKey: 'products.tabExpiration' },
    { key: 'inventory', labelKey: 'products.tabInventory' },
    { key: 'threshold', labelKey: 'products.tabThreshold' },
    { key: 'history', labelKey: 'products.tabHistory' },
  ];

  return (
    <div>
      <div className="row row--between">
        <div>
          <button className="btn btn--ghost btn--sm" onClick={() => navigate('/products')}>
            ← {t('nav.products')}
          </button>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>
            {product.name}{' '}
            {product.status === 'ARCHIVED' && (
              <span className="badge badge--muted">{t('status.archived')}</span>
            )}
            {product.needsUnitSetup && (
              <span className="badge badge--warn">{t('products.needsUnitSetupBadge')}</span>
            )}
            {product.importPendingReview && (
              <span className="badge badge--info">{t('products.pendingReviewBadge')}</span>
            )}
          </h1>
          <p className="field-hint">{product.sku}</p>
          {canEdit && product.importPendingReview && (
            <button
              className="btn btn--ghost btn--sm"
              disabled={dismissReview.isPending}
              onClick={() => dismissReview.mutate()}
            >
              {t('products.dismissReview')}
            </button>
          )}
        </div>
        {canEdit && product.status === 'ACTIVE' && (
          <button className="btn btn--danger btn--sm" onClick={() => archive.mutate()}>
            {t('products.archive')}
          </button>
        )}
        {canEdit && product.status === 'ARCHIVED' && (
          <button
            className="btn btn--primary btn--sm"
            disabled={restore.isPending}
            onClick={() => restore.mutate()}
          >
            {restore.isPending ? t('app.loading') : t('products.restore')}
          </button>
        )}
      </div>

      <div className="tabs">
        {tabs.map((tb) => (
          <button key={tb.key} aria-selected={tab === tb.key} onClick={() => setTab(tb.key)}>
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      <div className="section">
        {tab === 'basic' && (
          <BasicInfoTab product={product} canEdit={canEdit} onChanged={refresh} />
        )}
        {tab === 'units' && <UnitEditor product={product} canEdit={canEdit} />}
        {tab === 'suppliers' && (
          <SuppliersTab product={product} canEdit={canEdit} onChanged={refresh} />
        )}
        {tab === 'expiration' && (
          <ExpirationTab product={product} canEdit={canEdit} onChanged={refresh} />
        )}
        {tab === 'inventory' && <ProductInventoryTab product={product} />}
        {tab === 'threshold' && <ProductThresholdTab product={product} />}
        {tab === 'history' && <ProductHistoryTab product={product} />}
      </div>
    </div>
  );
}

function BasicInfoTab({
  product,
  canEdit,
  onChanged,
}: {
  readonly product: ProductDetail;
  readonly canEdit: boolean;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [name, setName] = useState(product.name);
  const [brand, setBrand] = useState<ComboboxOption | null>(
    product.brandId ? { id: product.brandId, label: product.brandName ?? '' } : null,
  );
  const [category, setCategory] = useState<ComboboxOption | null>(
    product.categoryId ? { id: product.categoryId, label: product.categoryName ?? '' } : null,
  );
  const [description, setDescription] = useState(product.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const [newBarcode, setNewBarcode] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/products/${product.id}`, {
        method: 'PATCH',
        body: {
          name: name.trim(),
          brandId: brand?.id ?? null,
          categoryId: category?.id ?? null,
          description: description.trim() || undefined,
          version: product.version,
        },
      }),
    onSuccess: () => onChanged(),
    onError: (err) =>
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err)),
  });

  const addBarcode = useMutation({
    mutationFn: () =>
      apiFetch(`/products/${product.id}/barcodes`, {
        method: 'POST',
        body: { barcode: newBarcode.trim() },
      }),
    onSuccess: () => {
      setNewBarcode('');
      onChanged();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err)),
  });

  const archiveBarcode = useMutation({
    mutationFn: (barcodeId: string) =>
      apiFetch(`/products/${product.id}/barcodes/${barcodeId}/archive`, { method: 'POST' }),
    onSuccess: () => onChanged(),
  });

  const uploadImage = useMutation({
    mutationFn: async (file: File) => {
      const dataBase64 = await fileToBase64(file);
      return apiFetch(`/products/${product.id}/images`, {
        method: 'POST',
        body: { filename: file.name, contentType: file.type, dataBase64, kind: 'PRIMARY' },
      });
    },
    onSuccess: () => onChanged(),
    onError: (err) =>
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err)),
  });

  const deleteImage = useMutation({
    mutationFn: (imageId: string) =>
      apiFetch(`/products/${product.id}/images/${imageId}/delete`, { method: 'POST' }),
    onSuccess: () => onChanged(),
  });

  return (
    <div>
      <div className="row" style={{ gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        {product.images.map((img) => (
          <div key={img.id} style={{ position: 'relative' }}>
            <img
              src={img.url}
              alt={img.alt ?? ''}
              style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6 }}
            />
            {canEdit && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                style={{ position: 'absolute', top: 0, right: 0 }}
                onClick={() => deleteImage.mutate(img.id)}
              >
                ×
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => fileInputRef.current?.click()}
            >
              {t('products.addImage')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadImage.mutate(file);
                e.target.value = '';
              }}
            />
          </>
        )}
      </div>

      <form
        style={{ marginTop: 'var(--space-4)' }}
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label className="field">
          <span>{t('products.name')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canEdit}
            required
          />
        </label>
        <label className="field">
          <span>{t('products.brand')}</span>
          <Combobox
            value={brand}
            onChange={setBrand}
            fetchOptions={(q) => searchPresets('/brands', q)}
            onCreate={(n) => createPreset('/brands', n)}
            placeholder={t('products.pickBrand')}
            disabled={!canEdit}
          />
        </label>
        <label className="field">
          <span>{t('products.category')}</span>
          <Combobox
            value={category}
            onChange={setCategory}
            fetchOptions={(q) => searchPresets('/product-categories', q)}
            onCreate={(n) => createPreset('/product-categories', n)}
            placeholder={t('products.pickCategory')}
            disabled={!canEdit}
          />
        </label>
        <label className="field">
          <span>{t('products.description')}</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={!canEdit}
            rows={3}
          />
        </label>
        {error && <p className="field-error">{error}</p>}
        {canEdit && (
          <button type="submit" className="btn btn--primary btn--sm" disabled={save.isPending}>
            {t('app.save')}
          </button>
        )}
      </form>

      <div className="section">
        <h3>{t('products.barcodes')}</h3>
        <div className="table-scroll"><table className="table">
          <tbody>
            {product.barcodes.map((b) => (
              <tr key={b.id} style={b.archivedAt ? { opacity: 0.5 } : undefined}>
                <td>{b.barcode}</td>
                <td>{b.kind}</td>
                <td>
                  {b.isPrimary && (
                    <span className="badge badge--muted">{t('products.barcodePrimary')}</span>
                  )}
                </td>
                <td>
                  {canEdit && !b.archivedAt && (
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() => archiveBarcode.mutate(b.id)}
                    >
                      {t('units.archive')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
        {canEdit && (
          <form
            className="row"
            style={{ gap: 'var(--space-2)' }}
            onSubmit={(e) => {
              e.preventDefault();
              addBarcode.mutate();
            }}
          >
            <input
              className="field"
              placeholder={t('products.addBarcodePlaceholder')}
              value={newBarcode}
              onChange={(e) => setNewBarcode(e.target.value)}
            />
            <button type="submit" className="btn btn--ghost btn--sm" disabled={!newBarcode.trim()}>
              {t('app.save')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function SuppliersTab({
  product,
  canEdit,
  onChanged,
}: {
  readonly product: ProductDetail;
  readonly canEdit: boolean;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [supplier, setSupplier] = useState<ComboboxOption | null>(null);

  const attach = useMutation({
    mutationFn: () =>
      apiFetch(`/products/${product.id}/suppliers`, {
        method: 'POST',
        body: { supplierId: supplier?.id },
      }),
    onSuccess: () => {
      setSupplier(null);
      onChanged();
    },
  });
  const detach = useMutation({
    mutationFn: (supplierId: string) =>
      apiFetch(`/products/${product.id}/suppliers/${supplierId}/detach`, { method: 'POST' }),
    onSuccess: () => onChanged(),
  });

  return (
    <div>
      <div className="table-scroll"><table className="table">
        <thead>
          <tr>
            <th>{t('suppliers.colName')}</th>
            <th>{t('products.supplierSku')}</th>
            <th>{t('products.leadTime')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {product.suppliers.map((s) => (
            <tr key={s.id}>
              <td>
                {s.supplierName}{' '}
                {s.isDefault && (
                  <span className="badge badge--muted">{t('products.supplierDefault')}</span>
                )}
              </td>
              <td>{s.supplierSku ?? '—'}</td>
              <td>{s.leadTimeDays ?? '—'}</td>
              <td>
                {canEdit && (
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => detach.mutate(s.supplierId)}
                  >
                    {t('products.detach')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
      {product.suppliers.length === 0 && (
        <div className="empty-state">
          <p>{t('empty.noResults')}</p>
        </div>
      )}
      {canEdit && (
        <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <Combobox
            value={supplier}
            onChange={setSupplier}
            fetchOptions={async (q) => {
              const res = await apiFetch<{ items: SupplierItem[] }>(
                `/suppliers?query=${encodeURIComponent(q)}`,
              );
              return res.items.map((s) => ({ id: s.id, label: s.name }));
            }}
            placeholder={t('products.pickSupplier')}
          />
          <button
            className="btn btn--ghost btn--sm"
            disabled={!supplier}
            onClick={() => attach.mutate()}
          >
            {t('app.save')}
          </button>
        </div>
      )}
    </div>
  );
}

function ExpirationTab({
  product,
  canEdit,
  onChanged,
}: {
  readonly product: ProductDetail;
  readonly canEdit: boolean;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [tracksExpiration, setTracksExpiration] = useState(product.tracksExpiration);
  const [days, setDays] = useState(product.defaultShelfLifeDays?.toString() ?? '');
  const [basis, setBasis] = useState(product.shelfLifeBasis ?? 'MANUFACTURE');

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/products/${product.id}`, {
        method: 'PATCH',
        body: {
          tracksExpiration,
          defaultShelfLifeDays: days ? Number(days) : undefined,
          shelfLifeBasis: basis,
          version: product.version,
        },
      }),
    onSuccess: () => onChanged(),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <label className="row">
        <input
          type="checkbox"
          checked={tracksExpiration}
          onChange={(e) => setTracksExpiration(e.target.checked)}
          disabled={!canEdit}
        />
        {t('products.tracksExpiration')}
      </label>
      {tracksExpiration && (
        <>
          <label className="field">
            <span>{t('products.defaultShelfLifeDays')}</span>
            <input
              value={days}
              onChange={(e) => setDays(e.target.value)}
              disabled={!canEdit}
              inputMode="numeric"
            />
          </label>
          <label className="field">
            <span>{t('products.shelfLifeBasis')}</span>
            <select
              value={basis}
              onChange={(e) => setBasis(e.target.value as 'MANUFACTURE' | 'RECEIPT')}
              disabled={!canEdit}
            >
              <option value="MANUFACTURE">{t('products.shelfLifeManufacture')}</option>
              <option value="RECEIPT">{t('products.shelfLifeReceipt')}</option>
            </select>
          </label>
        </>
      )}
      {canEdit && (
        <button type="submit" className="btn btn--primary btn--sm" disabled={save.isPending}>
          {t('app.save')}
        </button>
      )}
    </form>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
